import { Component, ElementRef, EventEmitter, HostListener, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Platform } from '@angular/cdk/platform';
import { parse } from 'postcode';

import { Address } from '../../../fact-find/services/fact-find-api.service';
import {
  PostcodeLookupResult,
  PostcodeLookupResultAddress,
  PostcodeLookupService
} from '../../../root/services/postcode-lookup.service';
import { of, Subject } from 'rxjs';
import {
  debounceTime,
  filter,
  distinctUntilChanged,
  switchMap,
  tap,
  catchError,
  timeout,
  finalize
} from 'rxjs/operators';

const lookupTimeout = 3000;

interface AddressListItem {
  address: Partial<Address>;
  label: string;
}

function resultToAddressListItems(result: PostcodeLookupResult): AddressListItem[] {
  if (!result || !result.addresses) {
    return [];
  }

  const addresses = result.addresses.map((a) => {
    const address: Partial<Address> = {
      house: a.building_name || a.building_number,
      street: a.thoroughfare,
      line2: a.sub_building_name,
      city: a.town_or_city,
      country: 'UK' // a.country
    };

    if (!address.house) {
      address.house = a.sub_building_name;
      address.line2 = '';
    }

    if (!address.line2) {
      address.line2 = a.locality;
    }

    return address;
  });

  const items: AddressListItem[] = addresses.map((address) => {
    let label = `${address.house} ${address.street}`;

    if (address.line2) {
      label = `${label}, ${address.line2}`;
    }

    return { address, label };
  });

  return items;
}

@Component({
  selector: 'hfc-postcode',
  templateUrl: './postcode.component.html',
  styleUrls: ['./postcode.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PostcodeComponent,
      multi: true
    }
  ]
})
export class PostcodeComponent implements ControlValueAccessor {
  public searchTerm = '';
  public searchTerm$ = new Subject<string>();
  public items: AddressListItem[];
  public isOpen = false;
  public isLoading = false;
  public isPostcodeValid: boolean = false;
  public isIosOpening = false;

  public get validationMessage(): string | null {
    if (this.searchTerm.length < 5) {
      return 'Start by typing your full postcode';
    }

    const postcodeToValidate = parse(this.searchTerm);

    if (!postcodeToValidate.valid) {
      return 'Invalid postcode format';
    }

    return null;
  }

  @Output()
  public addressSelect: EventEmitter<Address> = new EventEmitter<Address>();

  private touched = false;

  constructor(
    private el: ElementRef,
    private postcodeLookupService: PostcodeLookupService,
    private platform: Platform
  ) {
    this.searchTerm$
      .pipe(
        filter((t) => t.length >= 5),
        distinctUntilChanged(),
        debounceTime(400),
        tap(() => {
          this.isLoading = true;
          this.isPostcodeValid = parse(this.searchTerm).valid;
        }),
        filter(() => this.isPostcodeValid),
        switchMap((term: string) =>
          this.postcodeLookupService.lookup(term).pipe(
            timeout(lookupTimeout),
            catchError(() => of([])),
            finalize(() => {
              this.isLoading = false;
            })
          )
        )
      )
      .subscribe((addresses: PostcodeLookupResultAddress[]) => {
        this.items = resultToAddressListItems({
          postcode: this.searchTerm,
          addresses
        });

        this.items.push({
          label: "My address isn't here",
          address: { country: 'UK' }
        });
      });
  }

  @HostListener('document:click', ['$event.target'])
  public onDocumentClicked(target) {
    if (this.isOpen && !this.el.nativeElement.contains(target)) {
      this.isOpen = false;
    }
  }

  public onSearchTermChanged() {
    this.touched = true;
    this.searchTerm$.next(this.searchTerm);
    this.onChangeFn && this.onChangeFn(this.searchTerm.toUpperCase());
  }

  public onInputClicked() {
    this.isOpen = true;

    if (!this.touched && this.searchTerm.length >= 5) {
      this.touched = true;
      this.searchTerm$.next(this.searchTerm);
    }
  }

  public onItemClicked(item: AddressListItem) {
    this.addressSelect.next(item.address as any);
    this.isOpen = false;
  }

  public onCancelClicked() {
    this.isOpen = false;
  }

  public onInputBlur() {
    this.onTouchedFn && this.onTouchedFn();
  }

  public onInputFocus() {
    if (this.platform.IOS) {
      this.isIosOpening = true;

      setTimeout(() => {
        window.scrollTo(0, 0);
        this.isIosOpening = false;
      }, 300);
    }
  }

  private onChangeFn: (value: string) => void;
  private onTouchedFn: () => void;

  writeValue(obj: any): void {
    this.searchTerm = obj || '';
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // throw new Error("Method not implemented.");
  }
}
